import React, { FC } from 'react';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import RstChart from './chart/rstChart.component';
import { DatePicker } from '../../../../types/redux/pages/PagesStore';
import {
  getMostRecentWeekday,
  getWeekdayXMonthsAgo,
} from '../../../../utilities/dateUtilities';
import { useSelector } from 'react-redux';
import { createSpecificTitleBarDatePickerSelector } from '../../../../redux/pages/selectors';
import { useAdditionalTitleBarDatePicker } from '../../../../hooks/useAdditionalTitleBarComponents';
import dayjs from 'dayjs';
import { dateOneMonthAgo } from '../../../../utilities/dateFormatters';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useRstEvolutionPageModel } from './queries';

const RstEvolution: FC<FundInfoComponentProps> = ({ fundId, section }) => {
  const startDateWeekday = getWeekdayXMonthsAgo(1);
  const endDateWeekday = getMostRecentWeekday();

  const startDatePicker: DatePicker = {
    titleBarKey: 'startDate',
    titleBarTitle: 'Select Start Date: ',
    currentValue: dayjs(startDateWeekday).format('YYYY-MM-DD'),
    displayOnOverviewPage: false,
  };

  const endDatePicker: DatePicker = {
    titleBarKey: 'endDate',
    titleBarTitle: 'Select End Date: ',
    currentValue: dayjs(endDateWeekday).format('YYYY-MM-DD'),
    displayOnOverviewPage: false,
  };

  useAdditionalTitleBarDatePicker(startDatePicker);
  useAdditionalTitleBarDatePicker(endDatePicker);

  const startDate = useSelector(
    createSpecificTitleBarDatePickerSelector('startDate'),
  );
  const endDate = useSelector(
    createSpecificTitleBarDatePickerSelector('endDate'),
  );

  const startDateProper = startDate.length
    ? startDate[0]?.currentValue
    : dayjs(dateOneMonthAgo()).format('YYYY-MM-DD');

  const endDateProper = endDate.length
    ? endDate[0]?.currentValue
    : dayjs().format('YYYY-MM-DD');

  const datesReady = !!startDateProper && !!endDateProper;

  const showComplianceChart =
    section !== 'partii' && section !== 'qiaif' && datesReady;

  const evolutionQuery = useRstEvolutionPageModel(
    fundId,
    startDateProper,
    endDateProper,
    { enabled: datesReady },
  );

  const { height } = useWindowSize();
  const renderHeight = (height - 324) / 2;
  const renderWidth = '100%';

  return (
    <QueryBoundary
      isLoading={evolutionQuery.isLoading}
      isFetching={evolutionQuery.isFetching}
      isError={evolutionQuery.isError}
      error={evolutionQuery.error}
      data={evolutionQuery.data?.data}
      loadingMessages={['Loading reverse stress test evolution…']}
      noDataMessage="No reverse stress test evolution data available."
    >
      {(pageData) => (
        <>
          {showComplianceChart && (
            <RstChart
              chartPayload={pageData.ucits_data}
              isFetching={evolutionQuery.isFetching}
              error={evolutionQuery.error}
              title={`Liquidity Reverse Stress Test - ${section.includes('ucits')
                ? 'UCITS Compliance'
                : 'CSSF Risk Spreading Compliance'
                }`}
              dimensions={{ xs: 12 }}
              heightAndWidth={{ height: renderHeight, width: renderWidth }}
              fundId={fundId}
            />
          )}
          <RstChart
            chartPayload={pageData.risk_data}
            isFetching={evolutionQuery.isFetching}
            error={evolutionQuery.error}
            title="Liquidity Reverse Stress Test - Risk Compliance"
            dimensions={{ xs: 12 }}
            heightAndWidth={{ height: renderHeight, width: renderWidth }}
            fundId={fundId}
          />
        </>
      )}
    </QueryBoundary>
  );
};

export default RstEvolution;
